import {html, PolymerElement} from '@polymer/polymer/polymer-element.js';
import '@cbar/cbar-dropdown/cbar-dropdown.js';
import '@cbar/cbar-relationship-selector/cbar-relationship-selector.js';
import {RecursiveValidate} from '@cbar/cbar-recursive-validate-mixin/cbar-recursive-validate-mixin.js';

/**
 * `cbar-entity-relationship-selector`
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarEntityRelationshipSelector extends RecursiveValidate(PolymerElement) {
  static get template() {
    return html`
      <style>
        :host {
          display: block;
        }

        #entityDropdown {
          @apply --cbar-entity-relationship-selector-entity-dropdown;
        }

        #relationshipDropdown {
          @apply --cbar-entity-relationship-selector-relationship-dropdown;
        }
          
        [hidden]{
            display: none !important;
        }
      </style>

      <cbar-dropdown
        id="entityDropdown"
        label="[[entityType]]s"
        items="[[_displayedEntities]]"
        on-change="onSelectedEntityDropdownChanged"
        value="[[selectedEntity.id]]"
        disabled$="[[disabled]]"
        required$="[[entityRequired]]"
        fixedMenuPosition$=[[fixedMenuPosition]]
      ></cbar-dropdown>

      <template is="dom-if" if="[[selectedEntity]]">
        <cbar-relationship-selector
          id="relationshipDropdown"
          relationships=[[selectedEntity.availableRelationships]]
          required$="[[relationshipRequired]]"
          disabled$="[[disabled]]"  
          selected-relationship="{{selectedRelationship}}"
          show-start-date="[[_isTruthy(selectedStartDate)]]"
          show-end-date="[[_isTruthy(selectedEndDate)]]"
          selected-start-date="{{selectedStartDate}}"
          selected-end-date="{{selectedEndDate}}"
          relationships-label="[[relationshipsLabel]]"
          fixed-menu-position=[[fixedMenuPosition]]
        ></cbar-relationship-selector>
      </template>
    `;
  }

  static get properties() {
    return {
      /**
       * An array of CBAR entities to display.
       */
      entities: {
        type: Array,
        value: [],
      },

      /**
       * The entity type (typically 'Group' or 'Event'
       */
      entityType: String,

      /**
       * The currently selected entity in the dropdown
       */
      selectedEntity: {
        type: Object,
        notify: true,
        observer: '_selectedEntityChanged',
      },

      /**
       * The currently selected relationship in the dropdown
       */
      selectedRelationship: {
        type: Object,
        notify: true,
      },

      /**
       * The currently selected start date for the selected relationship
       */
      selectedStartDate: {
        type: Date,
        notify: true,
      },

      /**
       * The currently selected start date for the selected relationship
       */
      selectedEndDate: {
        type: Date,
        notify: true,
      },

      /**
       * The label to display on the relationships dropdown
       */
      relationshipsLabel: {
        type: String,
        value: "Relationships",
      },

      /**
       * Whether an entity is required to be selected.
       * Use in conjunction with the validate() method or an iron-form
       */
      entityRequired: {
        type: Boolean,
        value: false,
      },

      /**
       * Whether a relationship is required to be selected.
       * Use in conjunction with the validate() method or an iron-form
       */
      relationshipRequired: {
        type: Boolean,
        value: false,
      },

      fixedMenuPosition: {
        type: Boolean,
        value: false,
      },

      contextEntityId: {
        type: String,
        value: false
      },
      disabled: {
        type: Boolean,
        value: false
      }
    }

      ;
  }

  static get observers() {
    return [
      '_entitiesChanged(entities.splices)',
      '_handleContext(contextEntityId, entities.splices)'
    ];
  }

  get entityDropdown(){
    if(!this.__entityDropdown){
      this.__entityDropdown = this.root.querySelector('#entityDropdown');
    }
    return this.__entityDropdown;
  }

  static get firstOptionID() {
    return '__firstOpt';
  }

  _isTruthy(value) {
    return !!value;
  }

  _entitiesChanged(entitiesSplices) {
    if(!this.entities){
      return;
    }

    this._displayedEntities = this.entities.slice(0);

    if( this._displayedEntities && this._displayedEntities.length ) {
      if( this._displayedEntities[ 0 ].id !== this.constructor.firstOptionID ) {
        // Add a "Select entity" option as the first dropdown option
        this.unshift('_displayedEntities', {
          id: this.constructor.firstOptionID,
          title: 'Please Select',
        });
      }
    }
  }

  onSelectedEntityDropdownChanged(e) {
    const id = e.target.value;

    this.selectedEntity = this.entities.find(e => e.id.toString() === id.toString());
  }

  _selectedEntityChanged(selectedEntity) {
    if( selectedEntity && selectedEntity.id === this.constructor.firstOptionID ) {
      // First option selected, unset our selectedEntity
      return this.selectedEntity = undefined;
    }

    // Reset the selected relationship
    this.selectedRelationship = undefined;

    this.selectedStartDate = this.selectedEndDate = null;
  }

  _handleContext(contextId, entitySplices){
    if(!contextId || !this.entities || this.entities.length === 0){
      return;
    }

    let selectedEntity = this._displayedEntities.find(entity => entity.id === contextId);

    requestAnimationFrame(() => {
      this.selectedEntity = selectedEntity ? selectedEntity : {};
      this.entityDropdown.hidden = !!selectedEntity;
    });
  }
}

window.customElements.define('cbar-entity-relationship-selector', CbarEntityRelationshipSelector);
